type LandscapeViewportBaselineInput = {
	innerHeight?: number;
	visualViewportHeight?: number;
};

type LandscapeViewportTrustInput = {
	isLandscape: boolean;
	innerHeight?: number;
	visualViewportHeight?: number;
};

type LandscapeKeyboardVisibilityInput = {
	isLandscape: boolean;
	innerHeight?: number;
	landscapeBaselineHeight?: number;
	visualViewportHeight?: number;
};

function normalizeDimension(value?: number): number {
	return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function getBrowserWindow(): Window | undefined {
	return typeof window === "undefined" ? undefined : window;
}

function clampDimension(value: number, min: number, max: number): number {
	return Math.min(Math.max(value, min), max);
}

export type KeyboardSafeMenuDirection = "bottom" | "auto";

type KeyboardSafeMenuHeightOptions = {
	direction?: KeyboardSafeMenuDirection;
	viewportMargin?: number;
};

export function getKeyboardSafeMenuHeight(activatorRect: DOMRect | null, options: KeyboardSafeMenuHeightOptions = {}): number {
	const browserWindow = getBrowserWindow();
	const visualViewport = browserWindow?.visualViewport;
	const viewportHeight = normalizeDimension(visualViewport?.height ?? browserWindow?.innerHeight);
	const viewportTop = normalizeDimension(visualViewport?.offsetTop);
	const viewportBottom = viewportTop + viewportHeight;
	const direction = options.direction ?? "bottom";
	const viewportMargin = options.viewportMargin ?? 8;

	if (!activatorRect) {
		return Math.max(0, viewportHeight - viewportMargin);
	}

	const activatorTop = normalizeDimension(activatorRect.top);
	// Safari can return bottom=0 when an element is at the very top of the viewport;
	// fall back to top+height to handle that edge case.
	const activatorBottom = normalizeDimension(activatorRect.bottom) || activatorTop + normalizeDimension(activatorRect.height);

	if (activatorBottom < viewportTop) {
		return 0;
	}

	if (activatorTop >= viewportBottom) {
		return 0;
	}

	const boundedActivatorBottom = clampDimension(activatorBottom, viewportTop, viewportBottom);
	const availableBelow = Math.max(0, viewportBottom - viewportMargin - boundedActivatorBottom);

	if (direction === "auto") {
		const boundedActivatorTop = clampDimension(activatorTop, viewportTop, viewportBottom);
		const availableAbove = Math.max(0, boundedActivatorTop - viewportTop - viewportMargin);
		return Math.max(availableBelow, availableAbove);
	}

	// Menus open downward by default; using availableAbove would set a large maxHeight
	// that lets the list overflow into the on-screen keyboard. Only cap by what is below.
	return availableBelow;
}

export function getLandscapeViewportBaseline({ innerHeight, visualViewportHeight }: LandscapeViewportBaselineInput): number {
	return Math.max(normalizeDimension(innerHeight), normalizeDimension(visualViewportHeight));
}

export function shouldTrustLandscapeViewportBaseline({
	isLandscape,
	innerHeight,
	visualViewportHeight
}: LandscapeViewportTrustInput): boolean {
	if (!isLandscape) {
		return false;
	}

	const safeInnerHeight = normalizeDimension(innerHeight);
	const safeVisualViewportHeight = normalizeDimension(visualViewportHeight);

	if (!safeInnerHeight || !safeVisualViewportHeight) {
		return false;
	}

	return safeVisualViewportHeight >= safeInnerHeight * 0.9;
}

export function isLandscapeKeyboardVisible({
	isLandscape,
	innerHeight,
	landscapeBaselineHeight,
	visualViewportHeight
}: LandscapeKeyboardVisibilityInput): boolean {
	if (!isLandscape) {
		return false;
	}

	// Prefer an established baseline when available, but fall back to `innerHeight` so we can still
	// detect an already-open keyboard during a rotation (when the baseline is intentionally not trusted yet).
	const safeLandscapeBaselineHeight = normalizeDimension(landscapeBaselineHeight) || normalizeDimension(innerHeight);
	const safeVisualViewportHeight = normalizeDimension(visualViewportHeight);

	if (!safeLandscapeBaselineHeight || !safeVisualViewportHeight) {
		return false;
	}

	return safeVisualViewportHeight < safeLandscapeBaselineHeight * 0.75;
}
